import { Component } from 'preact';
import { connect } from 'unistore/preact';
import debounce from 'debounce';
import dayjs from 'dayjs';

import { WEBSOCKET_MESSAGE_TYPES } from '../../../../server/utils/constants';
import HistoryPage from './HistoryPage';
import { ALL_GROUPS } from './categoryGroups';

const PAGE_SIZE = 80;
const MAX_EVENTS_IN_MEMORY = 1000;
// Live states are buffered and flushed at this interval instead of triggering a
// render per websocket message. A chatty installation can emit hundreds of
// states per second; without batching, each one would run a full setState +
// timeline rebuild and freeze the page.
const LIVE_FLUSH_INTERVAL_MS = 1000;
// When a filter matches a very chatty device feature, a whole page of states is
// grouped into a single timeline row, so the page never fills the viewport, the
// infinite-scroll sentinel never leaves it, and each response would immediately
// trigger the next fetch, forever. Only allow this many sentinel-triggered loads
// without a user scroll in between; past that, the "load more" button takes over.
const MAX_AUTO_LOADS_WITHOUT_SCROLL = 3;

class History extends Component {
  getRooms = async () => {
    try {
      const rooms = await this.props.httpClient.get('/api/v1/room');
      this.setState({ rooms });
    } catch (e) {
      console.error(e);
    }
  };

  // Load all devices to be able to decorate live websocket
  // events (they only contain the device feature selector)
  getDevices = async () => {
    try {
      const devices = await this.props.httpClient.get('/api/v1/device');
      const featuresBySelector = new Map();
      devices.forEach(device => {
        device.features.forEach(feature => {
          featuresBySelector.set(feature.selector, {
            device_feature: {
              id: feature.id,
              name: feature.name,
              selector: feature.selector,
              category: feature.category,
              type: feature.type,
              unit: feature.unit,
              min: feature.min,
              max: feature.max
            },
            device: {
              name: device.name,
              selector: device.selector
            },
            room: device.room
              ? {
                  id: device.room.id,
                  name: device.room.name,
                  selector: device.room.selector
                }
              : null
          });
        });
      });
      this.featuresBySelector = featuresBySelector;
      this.setState({ featuresLoaded: true });
    } catch (e) {
      console.error(e);
    }
  };

  buildQueryParams = () => {
    const params = {
      take: PAGE_SIZE
    };
    const { selectedGroup, selectedRoomId, search } = this.state;
    if (selectedGroup) {
      const group = ALL_GROUPS.find(oneGroup => oneGroup.id === selectedGroup);
      if (group) {
        params.categories = group.categories.join(',');
      }
    }
    if (selectedRoomId) {
      params.room_id = selectedRoomId;
    }
    if (search && search.length) {
      params.search = search;
    }
    return params;
  };

  getEvents = async ({ reset = false } = {}) => {
    this.setState({ loading: true, error: false });
    if (reset) {
      // Drop any live states buffered under the previous filter/date context.
      this.liveEventBuffer = [];
    }
    try {
      const params = this.buildQueryParams();
      if (!reset && this.state.events.length > 0) {
        const lastEvent = this.state.events[this.state.events.length - 1];
        params.before = lastEvent.created_at;
        // Compound cursor: pass the last feature id so the server can break
        // created_at ties deterministically and never skip a state.
        if (lastEvent.device_feature && lastEvent.device_feature.id) {
          params.before_id = lastEvent.device_feature.id;
        }
      } else if (this.state.selectedDate) {
        // Jump to the end of the selected day, in the user's timezone
        params.before = dayjs(this.state.selectedDate)
          .endOf('day')
          .toISOString();
      }
      const newEvents = await this.props.httpClient.get('/api/v1/device_feature/states_history', params);
      this.setState(prevState => {
        let events;
        if (reset) {
          events = newEvents;
        } else {
          events = prevState.events.concat(newEvents);
          // Keep the array bounded during infinite scroll: drop the newest events
          // already scrolled past (array head) and keep the window being browsed.
          if (events.length > MAX_EVENTS_IN_MEMORY) {
            events = events.slice(events.length - MAX_EVENTS_IN_MEMORY);
          }
        }
        return {
          events,
          hasMore: newEvents.length === PAGE_SIZE,
          loading: false,
          initialized: true,
          pendingLiveEvents: reset ? [] : prevState.pendingLiveEvents
        };
      });
    } catch (e) {
      console.error(e);
      this.setState({ loading: false, error: true, initialized: true });
    }
  };

  refreshEvents = () => this.getEvents({ reset: true });

  loadMore = () => {
    // Explicit user action: re-arm the auto-load budget.
    this.autoLoadsWithoutScroll = 0;
    if (!this.state.loading && this.state.hasMore) {
      this.getEvents();
    }
  };

  autoLoadMore = () => {
    if (this.autoLoadsWithoutScroll >= MAX_AUTO_LOADS_WITHOUT_SCROLL) {
      return;
    }
    if (!this.state.loading && this.state.hasMore) {
      this.autoLoadsWithoutScroll += 1;
      this.getEvents();
    }
  };

  onUserScroll = () => {
    this.autoLoadsWithoutScroll = 0;
  };

  selectGroup = groupId => {
    this.setState(
      prevState => ({
        selectedGroup: prevState.selectedGroup === groupId ? null : groupId,
        expandedGroups: {}
      }),
      this.refreshEvents
    );
  };

  selectRoom = e => {
    this.setState({ selectedRoomId: e.target.value || null, expandedGroups: {} }, this.refreshEvents);
  };

  selectDate = e => {
    this.setState(
      { selectedDate: e.target.value || null, expandedGroups: {}, pendingLiveEvents: [] },
      this.refreshEvents
    );
  };

  backToLive = () => {
    this.setState({ selectedDate: null, expandedGroups: {}, pendingLiveEvents: [] }, this.refreshEvents);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  search = e => {
    this.setState({ search: e.target.value, expandedGroups: {} });
    this.debouncedRefreshEvents();
  };

  toggleExpand = groupKey => {
    this.setState(prevState => ({
      expandedGroups: {
        ...prevState.expandedGroups,
        [groupKey]: !prevState.expandedGroups[groupKey]
      }
    }));
  };

  matchCurrentFilters = event => {
    const { selectedGroup, selectedRoomId, search } = this.state;
    if (selectedGroup) {
      const group = ALL_GROUPS.find(oneGroup => oneGroup.id === selectedGroup);
      if (group && !group.categories.includes(event.device_feature.category)) {
        return false;
      }
    }
    if (selectedRoomId && (!event.room || event.room.id !== selectedRoomId)) {
      return false;
    }
    if (search && search.length && !event.device.name.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  };

  onNewState = payload => {
    // When browsing a past date, don't disturb the view with live events
    if (this.state.selectedDate) {
      return;
    }
    if (!this.featuresBySelector) {
      return;
    }
    const featureMetadata = this.featuresBySelector.get(payload.device_feature_selector);
    if (!featureMetadata) {
      return;
    }
    const event = {
      value: payload.last_value,
      created_at: payload.last_value_changed,
      ...featureMetadata
    };
    if (!this.matchCurrentFilters(event)) {
      return;
    }
    // Buffer the event (newest first) and schedule a flush instead of calling
    // setState on every websocket message. This keeps the page responsive even
    // when hundreds of states are received per second.
    this.liveEventBuffer.unshift(event);
    if (this.liveEventBuffer.length > MAX_EVENTS_IN_MEMORY) {
      this.liveEventBuffer.length = MAX_EVENTS_IN_MEMORY;
    }
    this.scheduleLiveFlush();
  };

  scheduleLiveFlush = () => {
    if (this.liveFlushTimeout) {
      return;
    }
    this.liveFlushTimeout = setTimeout(this.flushLiveEvents, LIVE_FLUSH_INTERVAL_MS);
  };

  flushLiveEvents = () => {
    this.liveFlushTimeout = null;
    if (this.liveEventBuffer.length === 0) {
      return;
    }
    const buffered = this.liveEventBuffer;
    this.liveEventBuffer = [];
    // When the user scrolled down in the feed, don't insert live events
    // at the top (it would move the content under their eyes). Store them
    // and display a "new events" button instead.
    const userIsAtTop = window.scrollY < 150;
    this.setState(prevState => {
      if (userIsAtTop) {
        return {
          events: [...buffered, ...prevState.events].slice(0, MAX_EVENTS_IN_MEMORY)
        };
      }
      return {
        pendingLiveEvents: [...buffered, ...prevState.pendingLiveEvents].slice(0, MAX_EVENTS_IN_MEMORY)
      };
    });
  };

  showPendingLiveEvents = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.setState(prevState => ({
      events: [...prevState.pendingLiveEvents, ...prevState.events].slice(0, MAX_EVENTS_IN_MEMORY),
      pendingLiveEvents: []
    }));
  };

  constructor(props) {
    super(props);
    this.state = {
      events: [],
      pendingLiveEvents: [],
      rooms: [],
      expandedGroups: {},
      selectedGroup: null,
      selectedRoomId: null,
      selectedDate: null,
      search: '',
      loading: true,
      initialized: false,
      hasMore: false,
      error: false,
      featuresLoaded: false
    };
    this.featuresBySelector = null;
    this.liveEventBuffer = [];
    this.liveFlushTimeout = null;
    this.autoLoadsWithoutScroll = 0;
    this.debouncedRefreshEvents = debounce(this.refreshEvents.bind(this), 300);
  }

  componentDidMount() {
    this.getEvents({ reset: true });
    this.getRooms();
    this.getDevices();
    this.props.session.dispatcher.addListener(WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE, this.onNewState);
    window.addEventListener('scroll', this.onUserScroll, { passive: true });
  }

  componentWillUnmount() {
    this.props.session.dispatcher.removeListener(WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE, this.onNewState);
    window.removeEventListener('scroll', this.onUserScroll);
    if (this.liveFlushTimeout) {
      clearTimeout(this.liveFlushTimeout);
      this.liveFlushTimeout = null;
    }
  }

  render(props, state) {
    return (
      <HistoryPage
        {...state}
        searchValue={state.search}
        user={props.user}
        selectGroup={this.selectGroup}
        selectRoom={this.selectRoom}
        selectDate={this.selectDate}
        backToLive={this.backToLive}
        search={this.search}
        loadMore={this.loadMore}
        autoLoadMore={this.autoLoadMore}
        toggleExpand={this.toggleExpand}
        showPendingLiveEvents={this.showPendingLiveEvents}
        featuresBySelector={this.featuresBySelector}
      />
    );
  }
}

export default connect('httpClient,session,user', {})(History);
